/**
 * Interactive-module GIF engine — port of the legacy __encodeGif + the five
 * generators (scrolling banner, countdown, price flip, scratch-reveal,
 * split-flap). Draw code is verbatim from the monolith.
 *
 * Output is a data-URL GIF that goes into the block's `gif` field; the block
 * renderer then emits a single email-safe <img>. THE GIF MUST BE HOSTED
 * (uploaded to a real URL) BEFORE SEND — data-URLs don't survive email
 * clients. Same contract as legacy.
 *
 * Encoder: gif.js (npm), 2x render scale for crisp text at 600px display.
 */
import GIF from "gif.js";
import { cdParts } from "../email/helpers";
import GIF_WORKER_SOURCE from "./gifWorkerSource";

/* The worker used to be referenced by URL (`PUBLIC_URL + "/gif.worker.js"`, the copy in
   public/). That resolves to `file:///gif.worker.js` in the file:// viewer, which a Worker
   cannot load ("cannot be accessed from origin null" — hero GIF drive, 2026-09-16), and it
   is one more request on the hosted app. The worker source now ships IN the bundle and runs
   from a blob: URL, created once, on every origin alike. `public/gif.worker.js` stays for
   anything else that links it. */
let workerUrl = "";
function gifWorkerUrl(): string {
  if (workerUrl) return workerUrl;
  try {
    workerUrl = URL.createObjectURL(new Blob([GIF_WORKER_SOURCE], { type: "application/javascript" }));
  } catch {
    workerUrl = "data:application/javascript;charset=utf-8," + encodeURIComponent(GIF_WORKER_SOURCE);
  }
  return workerUrl;
}

type Painter = (ctx: CanvasRenderingContext2D, frame: number, total: number) => void;

export function encodeGif(
  W: number,
  H: number,
  nframes: number,
  delay: number,
  painter: Painter,
  onProgress?: (pct: number) => void,
  scale = 2,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const CW = Math.round(W * scale), CH = Math.round(H * scale);
    const cv = document.createElement("canvas");
    cv.width = CW; cv.height = CH;
    const ctx = cv.getContext("2d")!;
    const gif = new GIF({ workers: 2, quality: 4, width: CW, height: CH, workerScript: gifWorkerUrl() });
    for (let f = 0; f < nframes; f++) {
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      painter(ctx, f, nframes);
      gif.addFrame(ctx, { copy: true, delay });
    }
    gif.on("progress", (p: number) => onProgress?.(Math.round(p * 100)));
    gif.on("finished", (blob: Blob) => {
      const rd = new FileReader();
      rd.onload = () => resolve(String(rd.result));
      rd.onerror = () => reject(new Error("read failed"));
      rd.readAsDataURL(blob);
    });
    try { gif.render(); } catch (e) { reject(e as Error); }
  });
}

function rrect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/* ── Scrolling Banner ── */
function sbSpeed(v?: string) {
  const s = parseInt(v || "", 10) || 16;
  if (s <= 10) return { f: 52, d: 120 };
  if (s >= 22) return { f: 34, d: 80 };
  return { f: 44, d: 100 };
}
export function genScrollBanner(d: { text?: string; tcolor?: string; speed?: string; dir?: string }, onProgress?: (p: number) => void) {
  const W = 600, H = 64, SP = sbSpeed(d.speed);
  const txt = d.text || " ", tc = d.tcolor || "#ffffff", dir = d.dir === "right" ? -1 : 1;
  const tmp = document.createElement("canvas").getContext("2d")!;
  tmp.font = "900 30px 'Arial Black',Arial,sans-serif";
  const unit = tmp.measureText(txt).width + 88;
  return encodeGif(W, H, SP.f, SP.d, (ctx, f) => {
    const off = dir * (f / SP.f) * unit, o = ((off % unit) + unit) % unit;
    ctx.fillStyle = "#1B2A58"; ctx.fillRect(0, 0, W, H);
    ctx.font = "900 30px 'Arial Black',Arial,sans-serif"; ctx.textBaseline = "middle"; ctx.textAlign = "left"; ctx.fillStyle = tc;
    for (let x = -o - unit; x < W + unit; x += unit) ctx.fillText(txt, x + 44, H / 2 + 1); // extra leading copy = seamless
  }, onProgress);
}

/* ── Countdown ── */
export function genCountdownGif(d: { lab?: string; deadline?: string }, onProgress?: (p: number) => void) {
  const r = cdParts(d.deadline), lab = (d.lab || "").toUpperCase(), W = 600, H = 120;
  return encodeGif(W, H, 10, 1000, (ctx, f) => {
    const s = (((r.s - f) % 60) + 60) % 60;
    ctx.fillStyle = "#1B2A58"; ctx.fillRect(0, 0, W, H); ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillStyle = "#C8A664"; ctx.font = "800 13px Arial"; ctx.fillText(lab, W / 2, 22);
    const g: [number, string][] = [[r.d, "DAYS"], [r.h, "HRS"], [r.m, "MIN"], [s, "SEC"]];
    const bw = 104, gap = 12, tot = g.length * bw + (g.length - 1) * gap, x0 = (W - tot) / 2;
    g.forEach((gr, i) => {
      const x = x0 + i * (bw + gap);
      ctx.fillStyle = "#243459"; rrect(ctx, x, 42, bw, 46, 8); ctx.fill();
      ctx.fillStyle = "#fff"; ctx.font = "900 28px Arial"; ctx.fillText((gr[0] < 10 ? "0" : "") + gr[0], x + bw / 2, 66);
      ctx.fillStyle = "#aab6d0"; ctx.font = "700 10px Arial"; ctx.fillText(gr[1], x + bw / 2, 100);
    });
  }, onProgress);
}

/* ── Price / Rate Flip ── */
export function genPriceGif(d: { from?: string; to?: string; unit?: string; lab?: string; sub?: string }, onProgress?: (p: number) => void) {
  const from = parseFloat(String(d.from).replace(/[^0-9.-]/g, "")) || 0;
  const to = parseFloat(String(d.to).replace(/[^0-9.-]/g, "")) || 0;
  const unit = d.unit || "", lab = (d.lab || "").toUpperCase(), sub = d.sub || "";
  const fmt = (v: number) => (unit === "$" ? "$" + Math.round(v).toLocaleString() : unit === "%" ? v.toFixed(2) + "%" : Math.abs(v % 1) > 0 ? v.toFixed(2) : String(Math.round(v)));
  const W = 600, H = 140, N = 34, hold = 6;
  return encodeGif(W, H, N, 70, (ctx, f) => {
    const k = Math.min(1, f / (N - hold)), e = 1 - Math.pow(1 - k, 3), v = from + (to - from) * e;
    ctx.fillStyle = "#F6F2EA"; ctx.fillRect(0, 0, W, H); ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillStyle = "#a9843f"; ctx.font = "800 13px Arial"; ctx.fillText(lab, W / 2, 26);
    ctx.fillStyle = "#1B2A58"; ctx.font = "900 46px Arial"; ctx.fillText(fmt(v), W / 2, 74);
    if (sub) { ctx.fillStyle = "#5a6472"; ctx.font = "600 14px Arial"; ctx.fillText(sub, W / 2, 114); }
  }, onProgress);
}

/* ── Scratch-to-Reveal (slower reveal per Jack: longer cover hold + slower wipe) ── */
export function genScratchGif(d: { cover?: string; lab?: string; offer?: string }, onProgress?: (p: number) => void) {
  const cover = (d.cover || "Scratch to reveal your savings").toUpperCase();
  const lab = (d.lab || "").toUpperCase(), offer = d.offer || "";
  const W = 600, H = 150, N = 46;
  return encodeGif(W, H, N, 90, (ctx, f) => {
    ctx.fillStyle = "#1B2A58"; ctx.fillRect(0, 0, W, H); ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillStyle = "#C8A664"; ctx.font = "800 13px Arial"; ctx.fillText(lab, W / 2, 50);
    ctx.fillStyle = "#fff"; ctx.font = "900 40px 'Arial Black',Arial"; ctx.fillText(offer, W / 2, 92);
    let p: number; if (f < 14) p = 0; else if (f > 36) p = 1; else p = (f - 14) / 22;
    const coverLeft = p * W;
    if (coverLeft < W - 1) {
      ctx.save(); ctx.beginPath(); ctx.rect(coverLeft, 0, W - coverLeft, H); ctx.clip();
      const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, "#e8cd7e"); g.addColorStop(1, "#c9a24a");
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      for (let i = 0; i < 60; i++) { ctx.fillStyle = "rgba(255,255,255," + (Math.random() * 0.5 + 0.2).toFixed(2) + ")"; ctx.beginPath(); ctx.arc(Math.random() * W, Math.random() * H, Math.random() * 1.4 + 0.3, 0, 7); ctx.fill(); }
      if (W - coverLeft > 210) { ctx.fillStyle = "#3a2f10"; ctx.font = "900 15px Arial"; ctx.fillText(cover, (coverLeft + W) / 2, H / 2); }
      ctx.restore();
    }
  }, onProgress);
}

/* ── Split-Flap board ── */
export function genSplitFlapGif(d: { lab?: string; word?: string }, onProgress?: (p: number) => void) {
  const lab = (d.lab || "").toUpperCase();
  const word = (d.word || "").toUpperCase().slice(0, 12);
  const chars = word.split(""), W = 600, H = 150, N = 26;
  return encodeGif(W, H, N, 80, (ctx, f) => {
    ctx.fillStyle = "#12203b"; ctx.fillRect(0, 0, W, H); ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillStyle = "#C8A664"; ctx.font = "800 12px Arial"; ctx.fillText(lab, W / 2, 36);
    const tw = 34, th = 46, gap = 5, n = chars.length || 1, total = n * tw + (n - 1) * gap, x0 = (W - total) / 2, ty = H / 2 + 16;
    chars.forEach((c, i) => {
      const x = x0 + i * (tw + gap);
      const start = 6 + i * 1.2, ph = (f - start) / 3;
      let sy = 1; if (ph >= 0 && ph <= 1) sy = Math.abs(Math.cos(ph * Math.PI));
      ctx.fillStyle = "#1f2a3d"; rrect(ctx, x, ty - th / 2, tw, th, 5); ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,.55)"; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(x, ty); ctx.lineTo(x + tw, ty); ctx.stroke();
      ctx.save(); ctx.translate(x + tw / 2, ty); ctx.scale(1, Math.max(0.05, sy));
      ctx.fillStyle = "#fff"; ctx.font = "900 26px 'Arial Black',Arial"; ctx.fillText(c, 0, 1); ctx.restore();
    });
  }, onProgress);
}

/** which generator serves which block type */
export const GIF_GENERATORS: Record<string, (d: Record<string, string>, onProgress?: (p: number) => void) => Promise<string>> = {
  scrollbanner: genScrollBanner,
  countdown: genCountdownGif,
  priceflip: genPriceGif,
  scratchreveal: genScratchGif,
  splitflap: genSplitFlapGif,
};
